import jwt_decode from 'jwt-decode';
import store from '../store/index';
import Vue from 'vue';
import Router from 'vue-router';
import { UPLOAD_THRESHOLDS } from '@/utils/uploadHelpers';

// Routes
import accounts from './modules/accounts';
import quotes from './modules/quotes';
import home from './modules/home';
import packages from './modules/packages';
import registration from './modules/registration';
import report from './modules/report';
import settings from './modules/settings';
import shoot from './modules/shoot';
import stripe from './modules/stripe';
import gallery from './modules/gallery';
import integrations from './modules/integrations';
import auth from './modules/auth';
import error from './modules/error';
import download from './modules/download';

import { getCookie, setCookie } from '@utils/cookies';

// Add comprehensive navigation duplicate prevention for all navigation methods
const originalPush = Router.prototype.push;
const originalReplace = Router.prototype.replace;

// Override push method to handle duplicates
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      return Promise.reject(err);
    }
    return Promise.resolve();
  });
};

// Override replace method to handle duplicates
Router.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      return Promise.reject(err);
    }
    return Promise.resolve();
  });
};

Vue.use(Router);

const routes = [...accounts, ...home, ...packages, ...registration, ...report, ...settings, ...shoot, ...stripe, ...quotes, ...gallery, ...integrations, ...auth, ...error, ...download];

const router = new Router({
  mode: 'history',
  routes
});

router.beforeEach((to, from, next) => {
  // Check for duplicate navigation early to prevent unnecessary processing
  if (to.path === from.path && to.name === from.name && JSON.stringify(to.query) === JSON.stringify(from.query) && JSON.stringify(to.params) === JSON.stringify(from.params)) {
    next(false); // Cancel the navigation
    return;
  }

  // Upload gatekeeping: prevent navigation during critical upload phase (0-33%)
  // Only check for logged-in users and exclude certain routes that should always be accessible
  const exemptRoutes = ['authLogin', 'authRegister', 'auth0Callback', 'notFound', 'serverError', 'stripeSuccess', 'stripeError', 'downloadShare'];

  if (!exemptRoutes.includes(to.name) && store.getters['user/getUser']) {
    const criticalPhaseUploads = store.getters['uploadProgress/uploads'].filter(upload => upload.progress < UPLOAD_THRESHOLDS.CRITICAL_PHASE && upload.status !== 'error');

    if (criticalPhaseUploads.length > 0) {
      // Show confirmation dialog for critical phase uploads
      const confirmNavigation = confirm('Your files are still in the initial upload phase. Leaving this page will cancel the uploads. Are you sure you want to continue?');

      if (!confirmNavigation) {
        next(false); // Cancel navigation
        return;
      }
      // If user confirms, continue with navigation (uploads will be cancelled by the store)
    }

    // Ensure video generation progress timer continues running during navigation
    // This prevents progress from stopping when navigating between pages
    store.dispatch('videoGenerationProgress/ensureProgressTimer');
  }

  const jwt = getCookie('jwt');
  const isAdmin = store.getters['user/isAdmin'];
  const user = store.getters['user/getUser'];
  let decoded_token;

  if (jwt) {
    decoded_token = jwt_decode(jwt);
    // Setting user on store
    store.dispatch('user/saveUser', { ...decoded_token });
    // Set user properties for Google Analytics tracking
    Vue.prototype.$gtag.set({
      user_properties: {
        name: decoded_token.name,
        role: decoded_token.role,
        company_name: decoded_token.company_name
      },
      user_id: decoded_token.id // Using subject claim as unique user ID
    });

    Vue.prototype.$customerio.identify({
      id: decoded_token.id,
      email: decoded_token.email,
      name: decoded_token.name
    });

    Vue.prototype.$amplitude.setUserId(decoded_token.id);
    Vue.prototype.$amplitude.setUserProperties({
      name: decoded_token.name,
      role: decoded_token.role,
      company_name: decoded_token.company_name,
      email: decoded_token.email,
      client_id: decoded_token.client_id
    });

    if (to.name === 'authLogin' || to.name === 'authRegister') {
      // Redirect to dashboard or home page instead
      next({ name: 'home' });
      return;
    }
  }

  if (
    to.name === 'phRegistration' ||
    to.name === 'businessRegistration' ||
    to.name === 'stripeSuccess' ||
    to.name === 'stripeError' ||
    to.name === 'authLogin' ||
    to.name === 'auth0Callback' ||
    to.name === 'authRegister' ||
    to.name === 'notFound' ||
    to.name === 'serverError' ||
    to.name === 'downloadShare'
  ) {
    next();
    return;
  }
  if (to.name === 'flashyShoots') {
    if (!jwt) {
      next();
      return;
    } else {
      // Check if the user is not an admin and is a B2C client
      if (!isAdmin && user.company_name && user.company_name.endsWith('B2C')) {
        next(); // Allow access for B2C clients and guests
        return;
      } else {
        // Redirect to the home page for admins and Bespoke clients
        window.location.replace(`${process.env.VUE_APP_FE_DOMAIN}/auth/login`);
        return;
      }
    }
  }

  /**
   * If the applicaton is running in FE dev env, skip token validation
   */
  if (process.env.VUE_APP_MOCK === 'true') {
    next();
    return;
  }
  // Retrieving the bearer token from the url
  const bearer_token = to.query.__bearer_access_token;
  /**
   * If the bearer token exists, it means we are redirecting from the login page so we need to:
   * 1. set the cookie
   * 2. set the logged user
   * 3. redirect to home page
   */
  if (bearer_token) {
    decoded_token = jwt_decode(bearer_token);
    // 1.
    const expiration = decoded_token.exp;
    setCookie({ cookieName: 'jwt', cookieValue: bearer_token, expiration });
    // 2.
    store.dispatch('user/saveUser', { ...decoded_token });
    // Set user properties for Google Analytics tracking
    Vue.prototype.$gtag.set({
      user_properties: {
        name: decoded_token.name,
        role: decoded_token.role,
        company_name: decoded_token.company_name
      },
      user_id: decoded_token.id // Using subject claim as unique user ID
    });
    // 3.
    Vue.prototype.$amplitude.setUserId(decoded_token.id);
    Vue.prototype.$amplitude.setUserProperties({
      name: decoded_token.name,
      role: decoded_token.role,
      company_name: decoded_token.company_name
    });

    next({
      name: 'home'
    });
    return;
  }

  // Fetch fresh user data if we're logged in and navigating to most pages
  // This ensures we always have the latest user data after page refreshes
  if (jwt && decoded_token && decoded_token.id && !['authLogin', 'authRegister', 'auth0Callback'].includes(to.name)) {
    // For routes that need fresh user data, wait for the API call to complete
    const needsFreshData = ['account', 'personalInformation'].includes(to.name) || to.path.includes('/profile') || to.path.includes('/account');

    if (needsFreshData) {
      // For critical routes, make the navigation wait for fresh data
      try {
        // Refresh user data using the UserApi
        const UserApi = require('@api/modules/user').UserApi;
        return UserApi.getUser({ id: decoded_token.id, role: decoded_token.role })
          .then(response => {
            if (response && response.data && response.data.user) {
              // Update the store with fresh user data
              store.dispatch('user/setUserData', { user: response.data.user });
              // Important: trigger our global refresh callback to update UI components
              if (window.userRefreshCallback) {
                window.userRefreshCallback();
              }
              // Now continue with navigation
              next();
            } else {
              next(); // Continue anyway if response is unexpected
            }
          })
          .catch(error => {
            console.error('Error refreshing user data:', error);
            next(); // Continue navigation even if there's an error
          });
      } catch (error) {
        console.error('Error loading UserApi:', error);
        next(); // Continue navigation
      }
    } else {
      // For non-critical routes, fetch data in the background
      try {
        const UserApi = require('@api/modules/user').UserApi;
        UserApi.getUser({ id: decoded_token.id, role: decoded_token.role })
          .then(response => {
            if (response && response.data && response.data.user) {
              // Update the store with fresh user data
              store.dispatch('user/setUserData', { user: response.data.user });
              // Trigger refresh callback
              if (window.userRefreshCallback) {
                window.userRefreshCallback();
              }
            }
          })
          .catch(error => console.error('Error refreshing user data:', error));
      } catch (error) {
        console.error('Error loading UserApi:', error);
      }
      next(); // Continue navigation immediately
    }
    return; // Stop here since we've called next() in all code paths
  }

  /**
   * If the cookies is not set, and we are not navigating to the login page, we need to redirect the user
   * to the login page
   */
  if (!jwt) {
    window.location.replace(`${process.env.VUE_APP_FE_DOMAIN}/auth/login`);
    return;
  }

  next();
});

// Optional: Add route tracking middleware
router.afterEach(to => {
  // Track pageview with enhanced data
  Vue.prototype.$gtag.pageview({
    page_path: to.path,
    page_title: to.name,
    page_location: window.location.href
  });
});

router.onError(error => {
  // Get current route name
  const currentRouteName = router.currentRoute.name;

  // Skip error page redirects for:
  // 1. Auth-related pages
  // 2. Already on error pages
  // 3. Component-level errors that can be handled locally
  const skipErrorPageRedirect = ['authLogin', 'authRegister', 'auth0Callback'].includes(currentRouteName) || ['notFound', 'serverError'].includes(currentRouteName);

  if (!skipErrorPageRedirect) {
    console.error('Router error:', error);

    // For component loading errors (lazy loading issues)
    if (error.name === 'ChunkLoadError' || error.code === 'CSS_CHUNK_LOAD_FAILED') {
      console.error('Failed to load application module:', error);
      // Use replace instead of push to avoid adding to history
      router.replace({ name: 'serverError' });
    }
    // For navigation failures
    else if (error.name === 'NavigationDuplicated') {
      // Just ignore duplicate navigation errors
      console.warn('Navigation duplicated, ignoring:', error);
    }
    // For other critical router errors
    else if (error.name === 'NavigationFailure') {
      console.error('Navigation failure:', error);
      router.replace({ name: 'notFound' });
    }
  } else {
    // Just log the error without redirecting
    console.warn('Error in skipped route:', currentRouteName, error);
  }
});

export default router;
